import {
  erpApiKeyQuery,
  useSetErpApiKeyMutation,
} from "@common/api/erp-api-keys"
import type { ErpApiKeySecretPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { Card } from "@common/components/ui/card"
import { CopyButton } from "@common/components/ui/copy-button"
import { Input } from "@common/components/ui/input"
import { Label } from "@common/components/ui/label"
import { getErrorMessage } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import { PageContent, PageTitle } from "@/components/page/page-content"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"

export const Route = createFileRoute(
  "/_layout/settings/_panel/integrations/erp/api-key/set",
)({
  staticData: { breadcrumb: "Generate API key" },
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(
      queryClient,
      "erp_config:update",
      "/settings/workspace/general",
    ),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(erpApiKeyQuery()),
  component: SetApiKeyPage,
})

function SetApiKeyPage(): React.ReactElement {
  const [created, setCreated] = useState<ErpApiKeySecretPublic>()

  return (
    <PageContent>
      <PageTitle title="Corridor API key" />
      <Card className="p-4 shadow-none">
        {created ? (
          <CreatedKey created={created} />
        ) : (
          <GenerateKey onCreate={setCreated} />
        )}
      </Card>
    </PageContent>
  )
}

function GenerateKey({
  onCreate,
}: {
  onCreate: (created: ErpApiKeySecretPublic) => void
}): React.ReactElement {
  const {
    data: { api_key: current },
  } = useSuspenseQuery(erpApiKeyQuery())
  const mutation = useSetErpApiKeyMutation()
  const showToast = useCustomToast()

  const generate = () =>
    mutation.mutate(
      {},
      {
        onSuccess: onCreate,
        onError: (error) => showToast("Error", getErrorMessage(error), "error"),
      },
    )

  return (
    <div className="space-y-4">
      <p className="text-small-regular text-muted-foreground">
        {current
          ? `This replaces the current key (${current.key_prefix}…). The Corridor sync stops importing until it uses the new key.`
          : "The Corridor sync sends this key in the X-API-Key header to import into this workspace."}
      </p>
      <div className="flex justify-end gap-2">
        <Button asChild variant="secondary">
          <Link to="/settings/integrations/erp">Cancel</Link>
        </Button>
        <Button onClick={generate} disabled={mutation.isPending}>
          {current ? "Replace API key" : "Generate API key"}
        </Button>
      </div>
    </div>
  )
}

function CreatedKey({
  created,
}: {
  created: ErpApiKeySecretPublic
}): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="api-key">API key</Label>
        <div className="flex items-center gap-1.5">
          <Input id="api-key" readOnly value={created.key} />
          <CopyButton content={created.key} copyMessage="API key copied." />
        </div>
        <p className="text-small-regular text-muted-foreground">
          Copy this key now. You won't be able to see it again. Send it in the
          X-API-Key header.
        </p>
      </div>
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/settings/integrations/erp">Done</Link>
        </Button>
      </div>
    </div>
  )
}
